// Artifacts: dropped by bosses, activated by spending resources. Activation
// applies a powerful run-scoped modifier. A boss drops a random artifact the
// run does not already hold; once every artifact is held, bosses stop dropping.

import type { GameState } from '@core/state';
import { recomputeModifiers } from '@core/state';
import { ARTIFACTS, ARTIFACT_BY_ID, activationCostAt } from '@data/artifacts';
import type { ResourceId } from '@data/resources';
import { bus } from '@core/events';
import { t, tc } from '@i18n/i18n';

/** Drops a random not-yet-held artifact (called on a boss defeat). */
export function dropArtifact(state: GameState): void {
  const held = new Set(state.run.artifacts.map((a) => a.id));
  const pool = ARTIFACTS.filter((a) => !held.has(a.id));
  if (pool.length === 0) return;
  const def = pool[Math.floor(Math.random() * pool.length)];
  state.run.artifacts.push({ id: def.id, activated: false });
  bus.emit('artifact:dropped', { id: def.id, name: def.name });
  bus.emit('milestone', {
    title: t('milestone.artifactFound', { name: tc('artifact.' + def.id + '.name', def.name) }),
    subtitle: t('milestone.artifactHint'),
    tone: 'epic',
  });
}

/** Activates a held artifact if its resource cost can be paid. */
export function activateArtifact(state: GameState, id: string): boolean {
  const art = state.run.artifacts.find((a) => a.id === id);
  if (!art || art.activated) return false;
  const def = ARTIFACT_BY_ID[id];
  if (!def) return false;

  // Cost scales with the current age (early-gate drops are affordable, deep
  // drops stay a real spend); the lab UI shows the same scaled numbers.
  const cost = Object.entries(activationCostAt(def, state.run.age)) as [ResourceId, number][];
  for (const [res, amt] of cost) {
    if (state.run.resources[res] < amt) return false;
  }
  for (const [res, amt] of cost) state.run.resources[res] -= amt;

  art.activated = true;
  recomputeModifiers(state.run);
  bus.emit('artifact:activated', { id, name: def.name });
  bus.emit('milestone', {
    title: t('milestone.artifactActive', { name: tc('artifact.' + id + '.name', def.name) }),
    tone: 'epic',
  });
  return true;
}
